import { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './config/firebase';

import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Companies from './pages/Companies';
import CompanyDetail from './pages/CompanyDetail';
import Users from './pages/Users';
import Settings from './pages/Settings';
import Admins from './pages/Admins';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Motivo por el que se cerró la sesión; el formulario de acceso lo muestra.
  // Antes se cerraba en silencio y el botón se quedaba en «Verificando...».
  const [denied, setDenied] = useState(null);

  // Una sola suscripción: con [navigate, location] como dependencias se volvía
  // a suscribir y a leer super_admins en cada cambio de página.
  useEffect(() => {
    return onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        setUser(null);
        setLoading(false);
        return;
      }
      try {
        const email = (currentUser.email || '').toLowerCase();
        const adminDoc = await getDoc(doc(db, 'super_admins', email));
        if (adminDoc.exists()) {
          setDenied(null);
          setUser(currentUser);
        } else {
          setDenied({ text: 'Esta cuenta no tiene acceso al panel de administración.', at: Date.now() });
          await signOut(auth);
        }
      } catch (error) {
        console.error('Error checking super admin:', error);
        // Las reglas solo dejan leer super_admins a un super admin: un rechazo
        // aquí significa que la cuenta no lo es.
        const text = error.code === 'permission-denied'
          ? 'Esta cuenta no tiene acceso al panel de administración.'
          : 'No se pudo verificar el acceso. Revisa tu conexión e inténtalo de nuevo.';
        setDenied({ text, at: Date.now() });
        await signOut(auth);
      } finally {
        setLoading(false);
      }
    });
  }, []);

  if (loading) {
    return (
      <div className="full-center">
        <div className="spinner-lg" />
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login notice={denied} />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <div className="app-shell">
      <Sidebar user={user} />
      <main className="app-main">
        <div className="app-main-content">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/companies" element={<Companies />} />
            <Route path="/companies/:id" element={<CompanyDetail />} />
            <Route path="/users" element={<Users />} />
            <Route path="/admins" element={<Admins currentEmail={user.email} />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

export default App;
